// Balances Due — every folio with money outstanding (or in credit).
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

const BalancesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await apiCall('/api/admin/finance/balances')); }
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  if (!data) return null;

  const rows = data.due.map((r) => (
    <tr key={r.booking_id} className="border-b last:border-0" data-testid={`balance-row-${r.booking_id}`}>
      <td className="py-2 px-3 font-medium text-slate-900">{r.guest_name}</td>
      <td className="py-2 px-3 text-slate-600">{r.room_name}</td>
      <td className="py-2 px-3 text-slate-600">{r.check_in} → {r.check_out}</td>
      <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status === 'checked_in' ? 'bg-green-100 text-green-700' : r.status === 'no_show' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{r.status.replace('_', ' ')}</span></td>
      <td className="py-2 px-3 text-slate-500 text-xs">{r.invoice_number || '—'}</td>
      <td className="py-2 px-3 text-right text-xs text-slate-500">{r.days_since_checkout != null && r.days_since_checkout >= 0 ? `${r.days_since_checkout}d` : ''}</td>
      <td className="py-2 px-3 text-right font-bold text-red-600">{eur(r.balance)}</td>
    </tr>
  ));

  const departing = data.departing_today_unpaid.map((r) => (
    <span key={r.booking_id} className="px-2 py-1 bg-white border border-red-200 rounded text-xs">{r.guest_name} · {r.room_name} · <b>{eur(r.balance)}</b></span>
  ));

  return (
    <div className="space-y-4" data-testid="balances-tab">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border p-4"><p className="text-xs text-slate-500">Total outstanding</p><p className="text-2xl font-bold text-red-600" data-testid="balances-total-due">{eur(data.total_due)}</p><p className="text-xs text-slate-400">{data.due.length} folios</p></div>
        <div className="bg-white rounded-xl border p-4"><p className="text-xs text-slate-500">Guest credit (overpaid)</p><p className="text-2xl font-bold text-emerald-600">{eur(data.total_credit)}</p><p className="text-xs text-slate-400">{data.credit.length} folios</p></div>
        <div className="bg-white rounded-xl border p-4 flex items-center justify-between"><div><p className="text-xs text-slate-500">Departing today unpaid</p><p className="text-2xl font-bold text-slate-900">{data.departing_today_unpaid.length}</p></div>
          <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100" title="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button></div>
      </div>
      {departing.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="balances-departing-today">
          <AlertTriangle className="w-4 h-4 text-red-600" /> <span className="text-sm font-semibold text-red-700">Settle before check-out:</span> {departing}
        </div>
      )}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr><th className="text-left py-2 px-3">Guest</th><th className="text-left py-2 px-3">Room</th><th className="text-left py-2 px-3">Stay</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Invoice</th><th className="text-right py-2 px-3">Overdue</th><th className="text-right py-2 px-3">Balance</th></tr>
          </thead>
          <tbody>{rows.length ? rows : <tr><td colSpan={7} className="py-8 text-center text-slate-400">Nothing outstanding — every folio is settled.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
};

export default BalancesTab;
